import { LitElement, TemplateResult, html } from 'lit';
import { query, property, state } from 'lit/decorators.js';
import { Constructor, ResizeDirection } from '../types/common/types';
import { SideNavEvents } from '../ui/gzPushBodyLayout/types';

// A mixin created for LitElement components
// https://lit.dev/docs/composition/mixins/#mixins-in-typescript
declare class ResizeMixinInterface {
  resizable: ResizeDirection;

  renderResizeHandler(): TemplateResult | undefined;
}

export const ResizableMixin = <SuperClass extends Constructor<LitElement>>(superClass: SuperClass) => {
  class Resizable extends superClass {
    @property({
      attribute: true,
      reflect: true,
    })
    resizable: ResizeDirection;

    @state()
    isResized = false;

    @state()
    width = 0;

    @state()
    height = 0;

    @state()
    originalWidth = 0;

    @state()
    elementWidth = 0;

    @state()
    elementHeight = 0;

    @state()
    originalHeight = 0;

    @state()
    originalMouseX = 0;

    @state()
    originalMouseY = 0;

    @state()
    originalElemY = 0;

    @query('#resizeHandler')
    resizeHandler: HTMLElement;

    @query('.resize-content')
    resizeElement: HTMLElement;

    updated(): void {
      if (this.originalWidth === 0 && this.resizeElement !== null) {
        this.originalWidth = this.resizeElement.offsetWidth;
        this.originalHeight = this.resizeElement.offsetHeight;
      }
    }

    private resizeContent() {
      this.resizeHandler.addEventListener('mousedown', event => {
        event.preventDefault();
        this.elementWidth = parseFloat(
          getComputedStyle(this.resizeElement, null).getPropertyValue('width').replace('px', '')
        );
        this.elementHeight = parseFloat(
          getComputedStyle(this.resizeElement, null).getPropertyValue('height').replace('px', '')
        );
        this.originalElemY = this.resizeElement.getBoundingClientRect().top;
        this.originalMouseX = event.pageX;
        this.originalMouseY = event.pageY;
        this.addEventListener('mousemove', this.resize);
        this.addEventListener('mouseup', this.stopResize);
        this.addEventListener('mouseleave', this.stopResize);

      });
    }

    private resizeConfig(element: HTMLElement, width: undefined | number, height: undefined | number) {
      element.setAttribute('style', (width ? 'width:' : 'height:') + (width ? width : height) + 'px');
    }

    private resize(event) {
      switch (this.resizable) {
        case ResizeDirection.LEFT:
          this.width = this.elementWidth - (event.pageX - this.originalMouseX);
          if (this.width > this.originalWidth) {
            this.resizeConfig(this.resizeElement, this.width, undefined);
          }

          break;
        case ResizeDirection.RIGHT:
          this.width = this.elementWidth + (event.pageX - this.originalMouseX);
          if (this.width > this.originalWidth) {
            this.resizeConfig(this.resizeElement, this.width, undefined);
          }
          break;
        case ResizeDirection.TOP:
          this.height = this.elementHeight - (event.pageY - this.originalMouseY);
          if (this.height > this.originalHeight) {
            this.resizeConfig(this.resizeElement, undefined, this.height);
            this.resizeElement.style.top = this.originalElemY + (event.pageY - this.originalMouseY) + 'px';
          }
          break;
        case ResizeDirection.BOTTOM:
          this.height = this.elementHeight + (event.pageY - this.originalMouseY);
          if (this.height > this.originalHeight) {
            this.resizeConfig(this.resizeElement, undefined, this.height);
          }
          break;
      }
      this.isResized = true;
    }

    private stopResize() {
      this.removeEventListener('mousemove', this.resize);
      if (this.isResized) {
        this.dispatchEvent(
          new CustomEvent(SideNavEvents.NAV_STOP_RESIZE, {
            detail: {
              navBarWidth: this.width,
            },
            bubbles: true,
            cancelable: true,
            composed: false,
          })
        );
        this.isResized = false;
      }
    }

    renderResizeHandler(): TemplateResult | undefined {
      return html`
        <div class="resize-handler" id="resizeHandler" @mouseover="${this.resizeContent}">
          <gz-icon class="resize-icon" color="secondary" name="ds-icon-resize_handle" size="20"></gz-icon>
        </div>
      `;
    }
  }

  return Resizable as Constructor<ResizeMixinInterface> & SuperClass;
};
